import { useState, useEffect, useCallback } from 'react';

import localStorage from 'views_shared/app/javascript/services/localStorage';

/**
 * useSidebar - persists the right sidebar open/closed state per user.
 *
 * Mirrors the useMailbox pattern. Desktop reads and writes the stored value;
 * mobile/tablet always start closed and don't persist (an explicit-open on
 * mobile shouldn't carry over to the next desktop session).
 *
 * @param {string} currentReceiverSlug - The current user's profile slug
 * @param {boolean} isDesktop - Whether the viewport is desktop-sized
 * @returns {{ sidebarExpanded: boolean, toggleSidebar: () => void }}
 */
function useSidebar(currentReceiverSlug, isDesktop) {
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    if (!isDesktop) {
      return false;
    }
    return localStorage.inboxSidebarExpanded.get(currentReceiverSlug, true);
  });

  // Close sidebar when resizing down to mobile/tablet
  useEffect(() => {
    if (!isDesktop) {
      setSidebarExpanded(false);
    }
  }, [isDesktop]);

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      if (isDesktop) {
        localStorage.inboxSidebarExpanded.set(currentReceiverSlug, next);
      }
      return next;
    });
  }, [isDesktop, currentReceiverSlug]);

  return { sidebarExpanded, toggleSidebar };
}

export default useSidebar;
